import { TrendingDown, TrendingUp } from "lucide-react";
import { type CSSProperties, useMemo, useRef, useState } from "react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, type TooltipProps, XAxis, YAxis } from "recharts";
import { ChartAreaSkeleton } from "@/components/loading";
import { CHART_COLORS, MAIN_BRAND_COLOR, resolveCssColor } from "@/lib/chart-colors";
import { EMPTY_CHART_MESSAGE } from "@/lib/empty-state-copy";
import { shouldShowSearchActivityOverlay, type SearchExecutionActivity } from "@/lib/search-activity";
import { cn } from "@/lib/utils";
import { calculateTrendFromPreviousPoint } from "./trend-utils";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip } from "../ui/chart";
import { SearchActivityOverlay } from "../search-activity-overlay";
import type { ChartBrand, ChartDataPoint } from "./types";

// Re-export for backward compatibility
export const COLORS = CHART_COLORS;

// Chart type options
export type ChartType = "area" | "line" | "glowing-line";

// Definition for a data series (line/area) in the chart
export interface DataSeriesConfig {
      suffix: string; // e.g., "_avg", "_best" - appended to LLM ID for dataKey
      label: string; // e.g., "Avg", "Best" - shown in tooltip and toggle
      strokeDasharray?: string; // e.g., "5 5" for dashed lines
      strokeWidth?: number;
      dotRadius?: number;
      fillOpacity?: number; // For area charts
}

// Configuration for the chart
export interface BaseChartConfig {
      title: string;
      tooltip?: React.ReactNode;
      description?: string;
      separatedHeader?: boolean;
      yAxisLabel: string;
      yAxisDomain: [number, number] | ["auto", "auto"];
      yAxisReversed?: boolean; // For ranking where lower is better
      valueFormatter?: (value: number | null, seriesLabel?: string) => string;
      valueSuffix?: string;
      // For charts that show null as "no data" vs 0
      treatNullAsNoData?: boolean;
      // Multiple data series per LLM (e.g., avg + best)
      dataSeries?: DataSeriesConfig[];
      // Show series toggles (for charts with multiple series like mention/ranking)
      showSeriesToggles?: boolean;
      // Chart type - defaults to "area"
      chartType?: ChartType;
      // Show trend badge
      showTrend?: boolean;
}

interface BaseChartProps {
      config: BaseChartConfig;
      chartData: ChartDataPoint[];
      brands: ChartBrand[];
      isLoading: boolean;
      activity?: SearchExecutionActivity | null;
      showElapsedTime?: boolean;
      showScanningCompetitorsOverlay?: boolean;
      className?: string;
      compact?: boolean;
      highlightedBrandId?: string | null;
      showCompetitors?: boolean;
      hiddenBrandIds?: ReadonlySet<string>;
}

// Default single series config
const DEFAULT_SERIES: DataSeriesConfig[] = [
      { suffix: "", label: "", strokeWidth: 1.5, dotRadius: 3, fillOpacity: 0.3 },
];
const EMPTY_HIDDEN_BRANDS = new Set<string>();

// Fixed chart height for consistency
const CHART_HEIGHT = 280;
const COMPACT_CHART_HEIGHT = 130;
// Fixed toggle area height for alignment
const TOGGLE_AREA_HEIGHT = 40;
const TOOLTIP_VIEWPORT_EDGE_PADDING = 16;
type ChartTooltipItem = NonNullable<TooltipProps<number, string>["payload"]>[number];
export const BaseChart = ({
      config,
      chartData,
      brands,
      isLoading,
      activity = null,
      showElapsedTime = true,
      showScanningCompetitorsOverlay = false,
      compact = false,
      highlightedBrandId = null,
      showCompetitors = true,
      hiddenBrandIds = EMPTY_HIDDEN_BRANDS,
}: BaseChartProps) => {
      const chartHeight = compact ? COMPACT_CHART_HEIGHT : CHART_HEIGHT;
      const showLoadingOverlay = shouldShowSearchActivityOverlay(activity, {
            includeScanning: showScanningCompetitorsOverlay,
      });
      const dataSeries = config.dataSeries ?? DEFAULT_SERIES;
      const showSeriesToggles = config.showSeriesToggles ?? false;
      const chartType = config.chartType ?? "area";
      const showSeparatedHeader = config.separatedHeader ?? false;
      const chartFrameRef = useRef<HTMLDivElement | null>(null);
      const [reverseTooltipX, setReverseTooltipX] = useState(false);
      const tooltipWidthEstimate = 430;

      // Track which series are visible (all visible by default)
      const [visibleSeries] = useState<Set<string>>(() => new Set(dataSeries.map((s) => s.suffix)));

      // Main brand is always visible; competitors respect shared dashboard visibility state.
      const visibleBrandIds = useMemo(
            () =>
                  new Set(
                        brands
                              .filter(
                                    (brand) =>
                                          brand.isMainBrand ||
                                          (showCompetitors && !hiddenBrandIds.has(brand.id)),
                              )
                              .map((brand) => brand.id),
                  ),
            [brands, hiddenBrandIds, showCompetitors],
      );
      const brandMap = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);

      // Get color for a brand - main brand uses main brand color, competitors get other colors
      const getBrandColor = useMemo(() => {
            // Skip first color (main brand) for competitors
            const resolvedMainBrandColor = resolveCssColor(MAIN_BRAND_COLOR);
            const competitorColors = CHART_COLORS.slice(1).map(resolveCssColor);
            let competitorIndex = 0;
            const colorMap = new Map<string, string>();

            brands.forEach((brand) => {
                  if (brand.isMainBrand) {
                        colorMap.set(brand.id, resolvedMainBrandColor);
                  } else {
                        colorMap.set(brand.id, competitorColors[competitorIndex % competitorColors.length]);
                        competitorIndex++;
                  }
            });

            return (brandId: string) => colorMap.get(brandId) ?? resolveCssColor(CHART_COLORS[0]);
      }, [brands]);

      // Get stroke width for a brand - main brand is thicker
      const getBrandStrokeWidth = (brandId: string, baseWidth: number): number => {
            const brand = brandMap.get(brandId);
            return brand?.isMainBrand ? baseWidth + 1 : baseWidth;
      };

      const effectiveHighlightedBrandId = useMemo(() => {
            if (!highlightedBrandId) return null;

            const highlightedBrand = brandMap.get(highlightedBrandId);
            if (!highlightedBrand || highlightedBrand.isMainBrand || !visibleBrandIds.has(highlightedBrandId)) {
                  return null;
            }

            return highlightedBrandId;
      }, [brandMap, highlightedBrandId, visibleBrandIds]);

      const getBrandOpacity = (brandId: string, defaultOpacity = 1): number => {
            if (!effectiveHighlightedBrandId) {
                  return defaultOpacity;
            }

            if (brandId === effectiveHighlightedBrandId) {
                  return 0.75;
            }

            return brandMap.get(brandId)?.isMainBrand ? 0.18 : 0.15;
      };

      // Calculate adaptive Y-axis domain based on actual data values
      // Applies to [0, 100], [-100, 100], and [1, 10] domains - adds appropriate padding
      const adaptiveYAxisDomain = useMemo<[number, number] | ["auto", "auto"]>(() => {
            const [minDomain, maxDomain] = config.yAxisDomain;

            // Check if this is an adaptable domain type
            const isPercentageDomain = minDomain === 0 && maxDomain === 100;
            const isSentimentDomain = minDomain === -100 && maxDomain === 100;
            const isRankingDomain = minDomain === 1 && maxDomain === 10;

            if (!isPercentageDomain && !isSentimentDomain && !isRankingDomain) {
                  return config.yAxisDomain;
            }

            // Find the min and max values across all data points and all brand series
            let minValue = Infinity;
            let maxValue = -Infinity;
            let hasData = false;

            chartData.forEach((point) => {
                  brands.forEach((brand) => {
                        dataSeries.forEach((series) => {
                              const key = `${brand.id}${series.suffix}`;
                              const value = point[key];
                              if (typeof value === "number") {
                                    hasData = true;
                                    if (value < minValue) minValue = value;
                                    if (value > maxValue) maxValue = value;
                              }
                        });
                  });
            });

            // If no data, return original domain
            if (!hasData) {
                  return config.yAxisDomain;
            }

            if (isPercentageDomain) {
                  // For [0, 100] domains: keep 0 as min, adapt max with padding of 5
                  const newMax = Math.min(Math.ceil(maxValue) + 5, 100);
                  return [0, newMax];
            }

            if (isSentimentDomain) {
                  // For [-100, 100] domains: adapt both min and max with padding of 5
                  const newMin = Math.max(Math.floor(minValue) - 5, -100);
                  const newMax = Math.min(Math.ceil(maxValue) + 5, 100);
                  return [newMin, newMax];
            }

            if (isRankingDomain) {
                  // For [1, 10] domains: keep 1 as min, adapt max with padding of 1
                  // (chart is reversed, so max value appears at bottom)
                  const newMax = Math.min(Math.ceil(maxValue) + 1, 10);
                  return [1, newMax];
            }

            return config.yAxisDomain;
      }, [config.yAxisDomain, chartData, brands, dataSeries]);

      // Build chart config for shadcn ChartContainer
      const chartConfig = useMemo<ChartConfig>(() => {
            const configObj: ChartConfig = {};
            brands.forEach((brand) => {
                  dataSeries.forEach((series) => {
                        const key = `${brand.id}${series.suffix}`;
                        configObj[key] = {
                              label: `${brand.name}${series.label ? ` (${series.label})` : ""}`,
                              color: getBrandColor(brand.id),
                        };
                  });
            });
            return configObj;
      }, [brands, dataSeries, getBrandColor]);

      // Check if there's any data
      const hasData = chartData.some((point) =>
            brands.some((brand) =>
                  dataSeries.some((series) => {
                        const dataKey = `${brand.id}${series.suffix}`;
                        const value = point[dataKey];
                        if (config.treatNullAsNoData) {
                              return value !== null && value !== undefined;
                        }
                        return typeof value === "number" && value > 0;
                  }),
            ),
      );

      // Calculate trend percentage from the previous bucket to the current bucket.
      const trend = useMemo(() => {
            if (!config.showTrend || chartData.length < 2 || brands.length === 0) return null;

            const mainBrand = brands.find((b) => b.isMainBrand);
            if (!mainBrand) return null;
            return calculateTrendFromPreviousPoint({
                  chartData,
                  brandId: mainBrand.id,
                  dataSeries,
                  yAxisReversed: config.yAxisReversed,
            });
      }, [chartData, brands, dataSeries, config.showTrend, config.yAxisReversed]);

      const formatValue = (value: number | null, seriesLabel?: string): string => {
            if (value === null || value === undefined) return "N/A";
            if (config.valueFormatter) return config.valueFormatter(value, seriesLabel);
            return `${value.toFixed(1)}${config.valueSuffix || ""}`;
      };

      const updateTooltipDirection = (coordinateX?: number) => {
            if (!chartFrameRef.current || typeof coordinateX !== "number" || typeof window === "undefined") {
                  setReverseTooltipX(false);
                  return;
            }

            const chartLeft = chartFrameRef.current.getBoundingClientRect().left;
            const cursorViewportX = chartLeft + coordinateX;
            const spaceToLeft = cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;
            const spaceToRight = window.innerWidth - cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;

            setReverseTooltipX(spaceToRight < tooltipWidthEstimate && spaceToLeft >= spaceToRight);
      };

      const trendBadge = trend ? (
            <Badge
                  variant="outline"
                  className={
                        trend.isPositive
                              ? "text-success bg-success-light border-success/20"
                              : "text-destructive bg-destructive/10 border-destructive/20"
                  }
            >
                  {trend.isPositive ? (
                        <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                        <TrendingDown className="h-3 w-3 mr-1" />
                  )}
                  {trend.value}%
            </Badge>
      ) : null;

      // Check if a line should be visible
      const isLineVisible = (brandId: string, seriesSuffix: string): boolean => {
            if (!visibleBrandIds.has(brandId)) return false;
            if (showSeriesToggles && dataSeries.length > 1) {
                  return visibleSeries.has(seriesSuffix);
            }
            return true;
      };

      // Generate unique gradient IDs for this chart instance
      const gradientIdPrefix = useMemo(() => `gradient-${Math.random().toString(36).substr(2, 9)}`, []);

      // Generate unique filter ID for glow effect
      const glowFilterId = useMemo(() => `glow-${Math.random().toString(36).substr(2, 9)}`, []);

      // Custom tooltip content with brand logos
      const CustomTooltipContent = ({ active, payload, label }: TooltipProps<number, string>) => {
            if (!active || !payload?.length) return null;
            const intervalLabel = (payload[0]?.payload as ChartDataPoint | undefined)?.intervalLabel ?? label;

            // Group and sort by value, main brand first then by value
            const sortedPayload = [...payload]
                  .filter((item: ChartTooltipItem) => {
                        const brandId = item.dataKey.split("_")[0] || item.dataKey;
                        const seriesSuffix = item.dataKey.includes("_")
                              ? "_" + item.dataKey.split("_").slice(1).join("_")
                              : "";
                        return isLineVisible(brandId, seriesSuffix);
                  })
                  .sort((a: ChartTooltipItem, b: ChartTooltipItem) => {
                        const aBrandId = a.dataKey.split("_")[0] || a.dataKey;
                        const bBrandId = b.dataKey.split("_")[0] || b.dataKey;
                        const aBrand = brands.find((b) => b.id === aBrandId);
                        const bBrand = brands.find((b) => b.id === bBrandId);

                        // Main brand always first
                        if (aBrand?.isMainBrand && !bBrand?.isMainBrand) return -1;
                        if (!aBrand?.isMainBrand && bBrand?.isMainBrand) return 1;

                        // Then sort by value
                        const aVal = a.value ?? 0;
                        const bVal = b.value ?? 0;
                        return config.yAxisReversed ? aVal - bVal : bVal - aVal;
                  });

            return (
                  <div className="bg-popover/95 backdrop-blur-sm border border-border/60 rounded-xl shadow-xl p-3 min-w-[150px] max-w-[calc(100vw-2rem)] sm:max-w-[26rem]">
                        <div className="text-xs font-medium text-muted-foreground mb-2.5 pb-2 border-b border-border/50">
                              {intervalLabel}
                        </div>
                        <div className="space-y-1.5">
                              {sortedPayload.map((item: ChartTooltipItem) => {
                                    const brandId = item.dataKey.split("_")[0] || item.dataKey;
                                    const brand = brands.find((b) => b.id === brandId);
                                    const series = dataSeries.find(
                                          (s) =>
                                                item.dataKey.endsWith(s.suffix) ||
                                                (!s.suffix && item.dataKey === brandId),
                                    );

                                    return (
                                          <div key={item.dataKey} className="flex items-start gap-2">
                                                <div
                                                      className="h-1.5 w-1.5 rounded-full shrink-0"
                                                      style={{ backgroundColor: item.color }}
                                                />
                                                {brand?.logo && (
                                                      <img
                                                            src={brand.logo}
                                                            alt={brand.name}
                                                            className="w-3.5 h-3.5 mt-0.5 shrink-0 rounded-sm"
                                                      />
                                                )}
                                                <span className="min-w-0 flex-1 text-xs leading-4 text-foreground/80 break-words">
                                                      {brand?.name || brandId}
                                                      {brand?.isMainBrand && " (You)"}
                                                      {series?.label && ` (${series.label})`}
                                                </span>
                                                <span className="text-xs font-semibold ml-auto shrink-0 tabular-nums">
                                                      {formatValue(item.value, series?.label)}
                                                </span>
                                          </div>
                                    );
                              })}
                        </div>
                  </div>
            );
      };

      // Render chart elements based on type
      const renderChartElements = () => {
            const elements: React.ReactNode[] = [];

            // Add gradient definitions for area charts
            if (chartType === "area") {
                  elements.push(
                        <defs key="gradients">
                              {brands.flatMap((brand) =>
                                    dataSeries.map((series) => {
                                          const key = `${brand.id}${series.suffix}`;
                                          const color = getBrandColor(brand.id);
                                          return (
                                                <linearGradient
                                                      key={`${gradientIdPrefix}-${key}`}
                                                      id={`${gradientIdPrefix}-${key}`}
                                                      x1="0"
                                                      y1="0"
                                                      x2="0"
                                                      y2="1"
                                                >
                                                      <stop
                                                            offset="5%"
                                                            stopColor={color}
                                                            stopOpacity={brand.isMainBrand ? 0.5 : 0.3}
                                                      />
                                                      <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                                                </linearGradient>
                                          );
                                    }),
                              )}
                        </defs>,
                  );
            }

            // Add glow filter definitions for glowing-line charts
            if (chartType === "glowing-line") {
                  elements.push(
                        <defs key="glow-filters">
                              {brands.flatMap((brand) =>
                                    dataSeries.map((series) => {
                                          const key = `${brand.id}${series.suffix}`;
                                          const color = getBrandColor(brand.id);
                                          return (
                                                <filter
                                                      key={`${glowFilterId}-${key}`}
                                                      id={`${glowFilterId}-${key}`}
                                                      x="-50%"
                                                      y="-50%"
                                                      width="200%"
                                                      height="200%"
                                                >
                                                      <feGaussianBlur
                                                            in="SourceGraphic"
                                                            stdDeviation="4"
                                                            result="blur1"
                                                      />
                                                      <feGaussianBlur
                                                            in="SourceGraphic"
                                                            stdDeviation="8"
                                                            result="blur2"
                                                      />
                                                      <feFlood floodColor={color} floodOpacity="0.6" result="color1" />
                                                      <feFlood floodColor={color} floodOpacity="0.3" result="color2" />
                                                      <feComposite
                                                            in="color1"
                                                            in2="blur1"
                                                            operator="in"
                                                            result="glow1"
                                                      />
                                                      <feComposite
                                                            in="color2"
                                                            in2="blur2"
                                                            operator="in"
                                                            result="glow2"
                                                      />
                                                      <feMerge>
                                                            <feMergeNode in="glow2" />
                                                            <feMergeNode in="glow1" />
                                                            <feMergeNode in="SourceGraphic" />
                                                      </feMerge>
                                                </filter>
                                          );
                                    }),
                              )}
                        </defs>,
                  );
            }

            // Render areas or lines for each brand and series
            // Render competitors first so main brand appears on top
            const sortedBrands = [...brands].sort((a, b) => {
                  if (a.isMainBrand && !b.isMainBrand) return 1; // Main brand last (renders on top)
                  if (!a.isMainBrand && b.isMainBrand) return -1;
                  return 0;
            });

            sortedBrands.forEach((brand) => {
                  dataSeries.forEach((series) => {
                        const key = `${brand.id}${series.suffix}`;
                        const color = getBrandColor(brand.id);
                        const isVisible = isLineVisible(brand.id, series.suffix);
                        const strokeWidth = getBrandStrokeWidth(brand.id, series.strokeWidth ?? 2);

                        if (chartType === "area") {
                              elements.push(
                                    <Area
                                          key={key}
                                          type="monotone"
                                          dataKey={key}
                                          stroke={color}
                                          strokeWidth={strokeWidth}
                                          strokeDasharray={series.strokeDasharray}
                                          fill={`url(#${gradientIdPrefix}-${key})`}
                                          fillOpacity={series.fillOpacity ?? 0.3}
                                          opacity={getBrandOpacity(brand.id, 1)}
                                          dot={false}
                                          activeDot={{
                                                r: series.dotRadius ?? 4,
                                                strokeWidth: 2,
                                                stroke: color,
                                                fill: "var(--background)",
                                          }}
                                          hide={!isVisible}
                                          connectNulls
                                    />,
                              );
                        } else if (chartType === "glowing-line") {
                              // Glowing line chart - smooth curves with glow effect
                              elements.push(
                                    <Line
                                          key={key}
                                          type="natural"
                                          dataKey={key}
                                          stroke={color}
                                          strokeWidth={brand.isMainBrand ? 2 : 1.5}
                                          strokeDasharray={series.strokeDasharray}
                                          dot={false}
                                          activeDot={{
                                                r: 3,
                                                strokeWidth: 0,
                                                fill: color,
                                                filter: `url(#${glowFilterId}-${key})`,
                                          }}
                                          opacity={getBrandOpacity(brand.id, 1)}
                                          hide={!isVisible}
                                          connectNulls
                                          filter={`url(#${glowFilterId}-${key})`}
                                    />,
                              );
                        } else {
                              elements.push(
                                    <Line
                                          key={key}
                                          type="monotone"
                                          dataKey={key}
                                          stroke={color}
                                          strokeWidth={strokeWidth}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          opacity={getBrandOpacity(brand.id, brand.isMainBrand ? 1 : 0.7)}
                                          dot={{
                                                r: 2.5,
                                                fill: color,
                                                strokeWidth: 1.5,
                                                stroke: "var(--background)",
                                          }}
                                          activeDot={{
                                                r: series.dotRadius ?? 4,
                                                strokeWidth: 2,
                                                stroke: "var(--background)",
                                                fill: color,
                                          }}
                                          hide={!isVisible}
                                          connectNulls
                                    />,
                              );
                        }
                  });
            });

            return elements;
      };

      const ChartComponent = chartType === "area" ? AreaChart : LineChart;

      return (
            <Card className={cn("relative w-full h-full flex flex-col overflow-visible shadow-sm rounded-xl", showSeparatedHeader && "p-0 gap-0")}>
                  {showSeparatedHeader ? (
                        <div className="flex h-12 items-center border-b px-5 shrink-0">
                              <div className="flex min-w-0 items-center gap-3">
                                    <CardTitle className="text-[15px] font-semibold">
                                          <span className="flex items-center gap-2">
                                                {config.title}
                                                {config.tooltip && config.tooltip}
                                          </span>
                                    </CardTitle>
                                    {trendBadge}
                              </div>
                        </div>
                  ) : (
                        <CardHeader className="pb-2">
                              <div className="flex items-center gap-3">
                                    <CardTitle className="text-lg font-semibold">
                                          <span className="flex items-center gap-2">
                                                {config.title}
                                                {config.tooltip && config.tooltip}
                                          </span>
                                    </CardTitle>
                                    {trendBadge}
                              </div>
                              {config.description && <CardDescription>{config.description}</CardDescription>}
                        </CardHeader>
                  )}
                  <CardContent className={cn("relative flex-1", showSeparatedHeader ? (compact ? "py-2" : "py-4") : "pt-0")}>
                        {showLoadingOverlay && (
                              <SearchActivityOverlay
                                    activity={activity}
                                    compact={compact}
                                    showElapsedTime={showElapsedTime}
                              />
                        )}

                        {isLoading && (
                              <ChartAreaSkeleton
                                    className="border-0 bg-transparent p-0"
                                    heightClassName="h-[calc(var(--chart-height)+var(--toggle-height))]"
                                    style={
                                          {
                                                "--chart-height": `${chartHeight}px`,
                                                "--toggle-height": `${TOGGLE_AREA_HEIGHT}px`,
                                          } as CSSProperties
                                    }
                              />
                        )}

                        {!isLoading && (
                              <>
                                    {/* Chart */}
                                    <div ref={chartFrameRef} className="w-full" style={{ height: chartHeight }}>
                                          {!hasData ? (
                                                <div
                                                      className="flex items-center justify-center text-muted-foreground"
                                                      style={{ height: chartHeight }}
                                                >
                                                      {!showLoadingOverlay && EMPTY_CHART_MESSAGE}
                                                </div>
                                          ) : (
                                                <ChartContainer config={chartConfig} className="h-full w-full">
                                                      <ChartComponent
                                                            data={chartData}
                                                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                                            onMouseEnter={(state) => {
                                                                  updateTooltipDirection(state.activeCoordinate?.x);
                                                            }}
                                                            onMouseLeave={() => setReverseTooltipX(false)}
                                                            onMouseMove={(state) => {
                                                                  updateTooltipDirection(state.activeCoordinate?.x);
                                                            }}
                                                      >
                                                            <CartesianGrid
                                                                  vertical={false}
                                                                  strokeDasharray="2 6"
                                                                  stroke="var(--border)"
                                                                  opacity={0.5}
                                                            />
                                                            <XAxis
                                                                  dataKey="label"
                                                                  tickLine={false}
                                                                  axisLine={false}
                                                                  tickMargin={10}
                                                                  className="text-xs"
                                                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                                            />
                                                            <YAxis
                                                                  tickLine={false}
                                                                  axisLine={false}
                                                                  tickMargin={8}
                                                                  className="text-xs"
                                                                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                                                  domain={adaptiveYAxisDomain}
                                                                  reversed={config.yAxisReversed}
                                                                  width={40}
                                                                  tickCount={5}
                                                                  tickFormatter={(value) =>
                                                                        Math.round(value).toString()
                                                                  }
                                                            />
                                                            <ChartTooltip
                                                                  allowEscapeViewBox={{ x: true, y: true }}
                                                                  cursor={{
                                                                        stroke: "var(--border)",
                                                                        strokeWidth: 1,
                                                                  }}
                                                                  content={<CustomTooltipContent />}
                                                                  reverseDirection={{ x: reverseTooltipX, y: false }}
                                                                  wrapperStyle={{ zIndex: 20 }}
                                                            />
                                                            {renderChartElements()}
                                                      </ChartComponent>
                                                </ChartContainer>
                                          )}
                                    </div>
                              </>
                        )}
                  </CardContent>
            </Card>
      );
};
